import React from 'react';
import { Paper, Box, Typography, IconButton, TextField, FormControlLabel, Switch, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// One feature inside the plan canvas. Boolean → nothing to configure.
// Limit → "Max <unit>" + Unlimited switch. Metered → number "per month" +
// Unlimited switch. `stats` (p95 etc.) helps GA pick a sane number.
const EntitlementCard = ({ feature, entitlement, stats, error, onChange, onRemove, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: feature.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const unlimited = entitlement.limit == null;
  const configurable = feature.kind !== 'boolean';

  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" data-testid={`entitlement-${feature.key}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, mb: 1 }}>
      <Box
        {...attributes}
        {...listeners}
        role="button"
        aria-roledescription="sortable feature"
        aria-label={t('Reorder {{name}}', { name: feature.name, defaultValue: `Reorder ${feature.name}` })}
        sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', touchAction: 'none' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2">{feature.name}</Typography>
        {configurable ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
            <TextField
              size="small"
              type="number"
              value={unlimited ? '' : entitlement.limit}
              disabled={unlimited}
              onChange={(e) => onChange({ ...entitlement, limit: e.target.value === '' ? 1 : Number(e.target.value) })}
              inputProps={{ min: 1, step: 1, 'aria-label': `${feature.name} ${t('limit', { defaultValue: 'limit' })}` }}
              error={Boolean(error)}
              helperText={error}
              sx={{ width: 120 }}
            />
            <Typography variant="body2" color="text.secondary">
              {feature.kind === 'metered'
                ? t('{{unit}} per month', { unit: feature.unit, defaultValue: `${feature.unit} per month` })
                : t('max {{unit}}', { unit: feature.unit, defaultValue: `max ${feature.unit}` })}
            </Typography>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={<Switch size="small" checked={unlimited} onChange={(e) => onChange({ ...entitlement, limit: e.target.checked ? null : (stats?.p95 || 100) })} />}
              label={<Typography variant="body2">{t('Unlimited', { defaultValue: 'Unlimited' })}</Typography>}
            />
            {stats && stats.companies > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('p95 usage on this plan: {{value}}', { value: stats.p95, defaultValue: `p95 usage on this plan: ${stats.p95}` })}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">{t('Included', { defaultValue: 'Included' })}</Typography>
        )}
      </Box>
      <Tooltip title={t('Remove from plan', { defaultValue: 'Remove from plan' })}>
        <IconButton size="small" onClick={onRemove} aria-label={t('Remove {{name}}', { name: feature.name, defaultValue: `Remove ${feature.name}` })}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default EntitlementCard;
